/**
 * Network id for speed-trading markets. The Hyperliquid speed-trading API only
 * operates on HyperEVM (999); markets, leverage, and prices are all keyed to it.
 */
export type NetworkId = number;

/**
 * A market price-stream symbol, e.g. "btc-usd" (always `<coin>-usd`, lowercase).
 * Derived from a coin so the legacy price-stream protocol keeps working.
 */
export type PairSymbol = string;

/**
 * A Hyperliquid perp coin, e.g. "BTC" (canonical market name, uppercase).
 */
export type Coin = string;

/**
 * Common network ids for reference.
 */
export const NETWORK_IDS = {
    /** Ethereum Mainnet */
    ETHEREUM: 1,
    /** Cronos Mainnet (default deposit origin) */
    CRONOS: 25,
    /** Polygon Mainnet */
    POLYGON: 137,
    /** HyperEVM — the speed-trading network */
    HYPER: 999,
} as const;

/** Default market when none is stored. */
export const DEFAULT_COIN: Coin = 'BTC';

/** `BTC` -> `btc-usd`. */
export function coinToPair(coin: Coin): PairSymbol {
    return `${coin.toLowerCase()}-usd`;
}

/**
 * `btc-usd` (or `BTC`) -> `BTC`. A namespaced HIP-3 coin ("xyz:SP500") has
 * already lost its canonical casing in the lowercase pair symbol (and its dex
 * name may itself contain `-`), so strip only the `-usd` suffix and keep it
 * lowercase — the backend resolves namespaced coins case-insensitively. Prefer
 * selecting by coin (`setSelectedCoin`) so the canonical name survives; this
 * derivation is the legacy fallback for pair-shaped inputs.
 */
export function pairToCoin(pair: PairSymbol): Coin {
    const base = pair.replace(/-usd$/i, '');
    if (base.includes(':')) {
        return base;
    }
    return (base.split(/[-/_]/)[0] ?? base).toUpperCase();
}

/**
 * Look up a market by coin, ignoring case.
 *
 * The canonical Hyperliquid name of a HIP-3 market keeps its casing after the
 * namespace ("xyz:TSLA"), but several selection writers still round-trip the
 * coin through the lowercase pair symbol (`coinToPair` -> `pairToCoin`), which
 * is deliberately lossy for namespaced coins and hands back "xyz:tsla". That
 * lowercase value gets persisted into the market selection (and carried on
 * trade rows), so an exact-case `.find()` silently misses
 * and the whole market goes dark: no leverage ladder, no meta columns, no
 * HIP-3 activation banner, dead Long/Short buttons. Compare case-insensitively
 * at every read site instead of trusting the stored casing.
 */
/**
 * The exchange's own spelling of a coin.
 *
 * Trades are stored with a lowercased `pairBase` ("link", "xyz:spcx"), and
 * `findMarket` is case-insensitive, so anything that only has to LOOK a market
 * up works fine with either. Hyperliquid's own API does not: a candle request
 * for "link" returns null where "LINK" returns bars, which is a blank chart
 * with no error anywhere. Anything handed to the exchange — or to the charting
 * library, which asks the exchange — has to come through here first.
 *
 * Falls back to the input so an unknown coin is still passed along rather than
 * silently blanked; the caller's own not-found path then handles it.
 */
export function canonicalCoin<T extends { coin: string }>(
    markets: readonly T[] | undefined,
    coin: string,
): string {
    return findMarket(markets, coin)?.coin ?? coin;
}

export function findMarket<T extends { coin: string }>(
    markets: readonly T[] | undefined,
    coin: string,
): T | undefined {
    if (!markets || !coin) return undefined;
    const target = coin.toLowerCase();
    return markets.find((m) => m.coin.toLowerCase() === target);
}
